import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { useNavigate, useParams } from "react-router-dom";

import { cvAdminApi } from "./api/cvAdminApi";
import { initials, safeMsg } from "./utils/cvUtils";

const PROFICIENCY_OPTIONS = [
  { value: "basic", label: "Basic" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "fluent", label: "Fluent" },
  { value: "native", label: "Native" },
];

// Responsive CSS-grid layouts (Box-based, so they don't depend on the MUI Grid
// version — the legacy <Grid item xs md> API is a no-op on MUI v7's Grid).
const GRID_3 = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
  gap: 2,
};
const GRID_2 = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
  gap: 2,
};
const FULL_SPAN = { gridColumn: "1 / -1" };

/** Normalize any date-ish value to YYYY-MM-DD for <input type="date">. */
function toDateInput(v) {
  if (!v) return "";
  const s = String(v);
  // Already an ISO-ish string -> take first 10 chars if they look like a date.
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const EMPTY_PROFILE = {
  full_name: "",
  email: "",
  phone: "",
  address: "",
  nationality: "",
  current_job_title: "",
  years_experience: "",
  relocate: false,
  expected_salary: "",
  current_salary: "",
  joining_availability_date: "",
  about_me: "",
  skills: "",
  notes: "",
};

function SectionCard({ icon, title, subtitle, action, children }) {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        bgcolor: alpha(theme.palette.background.paper, 0.92),
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 0%, ${alpha(theme.palette.info.main, 0.06)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.16),
            color: theme.palette.primary.main,
            width: 38,
            height: 38,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </Paper>
  );
}

function RepeatableRow({ index, onRemove, children }) {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        position: "relative",
        bgcolor: alpha(theme.palette.background.default, 0.5),
        borderColor: alpha(theme.palette.divider, 0.9),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800 }}>
          #{index + 1}
        </Typography>
        <Tooltip title="Remove">
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              color: theme.palette.error.main,
              bgcolor: alpha(theme.palette.error.main, 0.08),
              "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.16) },
            }}
          >
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      {children}
    </Paper>
  );
}

export default function CvEditPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams();

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [saving, setSaving] = useState(false);

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [certificates, setCertificates] = useState([]);

  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const notify = (severity, message) => setToast({ open: true, severity, message });
  const closeToast = () => setToast((p) => ({ ...p, open: false }));

  const backTo = `/cv/review/${userId}`;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const res = await cvAdminApi.getSubmission(userId);
      const d = res?.data || {};
      const p = d.profile || {};

      setUser(d.user || null);
      setProfile({
        full_name: p.full_name ?? "",
        email: p.email ?? "",
        phone: p.phone ?? "",
        address: p.address ?? "",
        nationality: p.nationality ?? "",
        current_job_title: p.current_job_title ?? "",
        years_experience: p.years_experience ?? "",
        relocate: Boolean(p.relocate),
        expected_salary: p.expected_salary ?? "",
        current_salary: p.current_salary ?? "",
        joining_availability_date: toDateInput(p.joining_availability_date),
        about_me: p.about_me ?? p.summary ?? "",
        skills: p.skills ?? "",
        notes: p.notes ?? "",
      });

      setExperiences(
        (d.experiences || []).map((e) => ({
          id: e.id,
          position: e.position ?? "",
          company: e.company ?? "",
          start_date: toDateInput(e.start_date),
          end_date: toDateInput(e.end_date),
          description: e.description ?? "",
        }))
      );

      setEducations(
        (d.educations || []).map((e) => ({
          id: e.id,
          degree: e.degree ?? "",
          institution: e.institution ?? "",
          graduation_date: toDateInput(e.graduation_date),
        }))
      );

      setLanguages(
        (d.languages || []).map((l) => ({
          id: l.id,
          language: l.language ?? "",
          proficiency: l.proficiency ?? "basic",
        }))
      );

      setCertificates(
        (d.certificates || []).map((c) => ({
          id: c.id,
          title: c.title ?? "",
          issuer: c.issuer ?? "",
          issue_date: toDateInput(c.issue_date),
          expiry_date: toDateInput(c.expiry_date),
        }))
      );
    } catch (err) {
      setLoadErr(safeMsg(err, "Failed to load submission."));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const setProfileField = (key) => (e) =>
    setProfile((p) => ({ ...p, [key]: e.target.value }));

  // Generic helpers for repeatable arrays.
  const updateItem = (setter) => (index, key, value) =>
    setter((arr) =>
      arr.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    );
  const removeItem = (setter) => (index) =>
    setter((arr) => arr.filter((_, i) => i !== index));

  const updateExperience = updateItem(setExperiences);
  const updateEducation = updateItem(setEducations);
  const updateLanguage = updateItem(setLanguages);
  const updateCertificate = updateItem(setCertificates);

  const addExperience = () =>
    setExperiences((arr) => [
      ...arr,
      { position: "", company: "", start_date: "", end_date: "", description: "" },
    ]);
  const addEducation = () =>
    setEducations((arr) => [...arr, { degree: "", institution: "", graduation_date: "" }]);
  const addLanguage = () =>
    setLanguages((arr) => [...arr, { language: "", proficiency: "basic" }]);
  const addCertificate = () =>
    setCertificates((arr) => [
      ...arr,
      { title: "", issuer: "", issue_date: "", expiry_date: "" },
    ]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await cvAdminApi.updateSubmission(userId, {
        profile,
        experiences,
        educations,
        languages,
        certificates,
      });
      notify("success", "Changes saved.");
      navigate(backTo);
    } catch (err) {
      notify("error", safeMsg(err, "Failed to save changes."));
      setSaving(false);
    }
  };

  const headerBg = useMemo(
    () =>
      `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(
        theme.palette.info.main,
        0.12
      )} 55%, ${alpha(theme.palette.success.main, 0.12)} 100%)`,
    [theme]
  );

  const displayName = user?.name || profile.full_name || "Employee";

  const dateFieldProps = {
    type: "date",
    fullWidth: true,
    size: "small",
    InputLabelProps: { shrink: true },
  };

  return (
    <Box sx={{ p: 2.5, maxWidth: 1100, mx: "auto" }}>
      {/* Hero / top bar */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          mb: 2.5,
          bgcolor: alpha(theme.palette.background.paper, 0.85),
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: headerBg,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Tooltip title="Back to submission">
                <IconButton
                  onClick={() => navigate(backTo)}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.9),
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>

              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  color: theme.palette.primary.main,
                  fontWeight: 800,
                  width: 48,
                  height: 48,
                }}
              >
                {initials(displayName)}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                  Edit CV — {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user?.employee_code ? `${user.employee_code} · ` : ""}
                  {user?.email || profile.email || ""}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(backTo)}
                disabled={saving}
                sx={{ borderRadius: 2.5, fontWeight: 800 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                startIcon={
                  saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />
                }
                onClick={handleSave}
                disabled={saving || loading || Boolean(loadErr)}
                sx={{ borderRadius: 2.5, fontWeight: 800 }}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </Stack>
          </Stack>
        </Box>

        {loading || saving ? <LinearProgress /> : null}
      </Paper>

      {loadErr ? (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 3 }} onClose={() => setLoadErr("")}>
          {loadErr}
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 8 }} spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading submission…
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          {/* Profile */}
          <SectionCard
            icon={<PersonOutlineRoundedIcon />}
            title="Profile"
            subtitle="Personal and professional details"
          >
            <Box sx={GRID_3}>
              <TextField
                label="Full name"
                value={profile.full_name}
                onChange={setProfileField("full_name")}
                fullWidth
                size="small"
              />
              <TextField
                label="Email"
                type="email"
                value={profile.email}
                onChange={setProfileField("email")}
                fullWidth
                size="small"
              />
              <TextField
                label="Phone"
                value={profile.phone}
                onChange={setProfileField("phone")}
                fullWidth
                size="small"
              />
              <TextField
                label="Current job title"
                value={profile.current_job_title}
                onChange={setProfileField("current_job_title")}
                fullWidth
                size="small"
              />
              <TextField
                label="Years of experience"
                type="number"
                value={profile.years_experience}
                onChange={setProfileField("years_experience")}
                fullWidth
                size="small"
              />
              <TextField
                label="Nationality"
                value={profile.nationality}
                onChange={setProfileField("nationality")}
                fullWidth
                size="small"
              />
              <TextField
                label="Expected salary"
                value={profile.expected_salary}
                onChange={setProfileField("expected_salary")}
                fullWidth
                size="small"
              />
              <TextField
                label="Current salary"
                value={profile.current_salary}
                onChange={setProfileField("current_salary")}
                fullWidth
                size="small"
              />
              <TextField
                label="Joining availability date"
                value={profile.joining_availability_date}
                onChange={setProfileField("joining_availability_date")}
                {...dateFieldProps}
              />

              <Box sx={FULL_SPAN}>
                <TextField
                  label="Address"
                  value={profile.address}
                  onChange={setProfileField("address")}
                  fullWidth
                  size="small"
                />
              </Box>

              <Box
                sx={{
                  ...FULL_SPAN,
                  display: "inline-flex",
                  alignItems: "center",
                  width: "fit-content",
                  pl: 1.5,
                  pr: 2,
                  py: 0.25,
                  borderRadius: 999,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={Boolean(profile.relocate)}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, relocate: e.target.checked }))
                      }
                    />
                  }
                  label="Willing to relocate"
                />
              </Box>

              <Box sx={FULL_SPAN}>
                <TextField
                  label="About me"
                  value={profile.about_me}
                  onChange={setProfileField("about_me")}
                  fullWidth
                  size="small"
                  multiline
                  minRows={4}
                />
              </Box>
              <Box sx={FULL_SPAN}>
                <TextField
                  label="Skills"
                  value={profile.skills}
                  onChange={setProfileField("skills")}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  helperText="Comma-separated or free text."
                />
              </Box>
              <Box sx={FULL_SPAN}>
                <TextField
                  label="Notes"
                  value={profile.notes}
                  onChange={setProfileField("notes")}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                />
              </Box>
            </Box>
          </SectionCard>

          {/* Experience */}
          <SectionCard
            icon={<WorkOutlineRoundedIcon />}
            title="Experience"
            subtitle={`${experiences.length} item(s)`}
            action={
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={addExperience}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Add
              </Button>
            }
          >
            {experiences.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No experience entries. Use “Add” to create one.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {experiences.map((exp, i) => (
                  <RepeatableRow
                    key={exp.id ?? `exp-${i}`}
                    index={i}
                    onRemove={() => removeItem(setExperiences)(i)}
                  >
                    <Box sx={GRID_2}>
                      <TextField
                        label="Position"
                        value={exp.position}
                        onChange={(e) => updateExperience(i, "position", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Company"
                        value={exp.company}
                        onChange={(e) => updateExperience(i, "company", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Start date"
                        value={exp.start_date}
                        onChange={(e) => updateExperience(i, "start_date", e.target.value)}
                        {...dateFieldProps}
                      />
                      <TextField
                        label="End date"
                        value={exp.end_date}
                        onChange={(e) => updateExperience(i, "end_date", e.target.value)}
                        {...dateFieldProps}
                      />
                      <Box sx={FULL_SPAN}>
                        <TextField
                          label="Description"
                          value={exp.description}
                          onChange={(e) => updateExperience(i, "description", e.target.value)}
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                        />
                      </Box>
                    </Box>
                  </RepeatableRow>
                ))}
              </Stack>
            )}
          </SectionCard>

          {/* Education */}
          <SectionCard
            icon={<SchoolOutlinedIcon />}
            title="Education"
            subtitle={`${educations.length} item(s)`}
            action={
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={addEducation}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Add
              </Button>
            }
          >
            {educations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No education entries. Use “Add” to create one.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {educations.map((edu, i) => (
                  <RepeatableRow
                    key={edu.id ?? `edu-${i}`}
                    index={i}
                    onRemove={() => removeItem(setEducations)(i)}
                  >
                    <Box sx={GRID_2}>
                      <TextField
                        label="Degree"
                        value={edu.degree}
                        onChange={(e) => updateEducation(i, "degree", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Institution"
                        value={edu.institution}
                        onChange={(e) => updateEducation(i, "institution", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Graduation date"
                        value={edu.graduation_date}
                        onChange={(e) => updateEducation(i, "graduation_date", e.target.value)}
                        {...dateFieldProps}
                      />
                    </Box>
                  </RepeatableRow>
                ))}
              </Stack>
            )}
          </SectionCard>

          {/* Languages */}
          <SectionCard
            icon={<TranslateRoundedIcon />}
            title="Languages"
            subtitle={`${languages.length} item(s)`}
            action={
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={addLanguage}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Add
              </Button>
            }
          >
            {languages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No languages. Use “Add” to create one.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {languages.map((lang, i) => (
                  <RepeatableRow
                    key={lang.id ?? `lang-${i}`}
                    index={i}
                    onRemove={() => removeItem(setLanguages)(i)}
                  >
                    <Box sx={GRID_2}>
                      <TextField
                        label="Language"
                        value={lang.language}
                        onChange={(e) => updateLanguage(i, "language", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        select
                        label="Proficiency"
                        value={lang.proficiency || "basic"}
                        onChange={(e) => updateLanguage(i, "proficiency", e.target.value)}
                        fullWidth
                        size="small"
                      >
                        {PROFICIENCY_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </RepeatableRow>
                ))}
              </Stack>
            )}
          </SectionCard>

          {/* Certificates */}
          <SectionCard
            icon={<WorkspacePremiumOutlinedIcon />}
            title="Certificates"
            subtitle={`${certificates.length} item(s)`}
            action={
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={addCertificate}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Add
              </Button>
            }
          >
            {certificates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No certificates. Use “Add” to create one.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {certificates.map((cert, i) => (
                  <RepeatableRow
                    key={cert.id ?? `cert-${i}`}
                    index={i}
                    onRemove={() => removeItem(setCertificates)(i)}
                  >
                    <Box sx={GRID_2}>
                      <TextField
                        label="Title"
                        value={cert.title}
                        onChange={(e) => updateCertificate(i, "title", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Issuer"
                        value={cert.issuer}
                        onChange={(e) => updateCertificate(i, "issuer", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Issue date"
                        value={cert.issue_date}
                        onChange={(e) => updateCertificate(i, "issue_date", e.target.value)}
                        {...dateFieldProps}
                      />
                      <TextField
                        label="Expiry date"
                        value={cert.expiry_date}
                        onChange={(e) => updateCertificate(i, "expiry_date", e.target.value)}
                        {...dateFieldProps}
                      />
                    </Box>
                  </RepeatableRow>
                ))}
              </Stack>
            )}
          </SectionCard>

          <Divider sx={{ my: 1 }} />

          {/* Footer actions (mirror of top bar) */}
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(backTo)}
              disabled={saving}
              sx={{ borderRadius: 2.5, fontWeight: 800 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />
              }
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2.5, fontWeight: 800 }}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </Stack>
        </Stack>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
